import BoldText from '@/components/BoldText';
import InformationBanner from '@/components/InformationBanner';
import { PlanMessage } from '@/components/PlanMessage';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { PricingPlan } from '@/constants/enum';
import { THUMBNAIL } from '@/helpers/link';
import {
  useDeleteAppearanceTextMutation,
  useGetWidgetAppearanceTextQuery,
  useUpdateAppearanceTextStatusMutation,
} from '@/redux/query';
import { handleVisibleBanner, isOpenLanguageTableInstructionBannerSelector } from '@/redux/slice/banner.slice';
import {
  handleSaveWidgetTextPaginationSettings,
  widgetTextDisableSelector,
  widgetTextPaginationSettingSelector,
  widgetTextTableSettingSelector,
} from '@/redux/slice/widgetText.slice';
import { ITextTable } from '@/types/components/translation';
import {
  BlockStack,
  Box,
  Button,
  Card,
  EmptyState,
  Icon,
  IndexTable,
  InlineStack,
  Pagination,
  useBreakpoints,
  useIndexResourceState,
} from '@shopify/polaris';
import { LanguageTranslateIcon, PlusIcon } from '@shopify/polaris-icons';
import { IndexTableHeading } from '@shopify/polaris/build/ts/src/components/IndexTable';
import { NonEmptyArray } from '@shopify/polaris/build/ts/src/types';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AddLanguageModal } from '../Modal/AddLanguageModal';
import { RowData } from './components/RowData';

interface Props {
  isLoadingTranslationTable: boolean;
  fullWidgetAppearanceText?: ITextTable[];
}

const ManageLanguage: FC<Props> = ({ isLoadingTranslationTable, fullWidgetAppearanceText }) => {
  const { t } = useTranslation('translation');
  const widgetTextTableSettings = useSelector(widgetTextTableSettingSelector);
  const widgetTextPaginationSetting = useSelector(widgetTextPaginationSettingSelector);
  const [isShowAddLanguageModal, setIsShowAddLanguageModal] = useState(false);
  const isOpenLanguageTableInstructionBanner = useSelector(isOpenLanguageTableInstructionBannerSelector);
  const dispatch = useDispatch();
  const widgetTextDisable = useSelector(widgetTextDisableSelector);

  const handleAddLanguage = useCallback(() => setIsShowAddLanguageModal(true), []);
  const [deleteAppearanceText, { isLoading: isLoadingDeleteAppearanceText }] = useDeleteAppearanceTextMutation();
  const [updateAppearanceTextStatus, { isLoading: isLoadingUpdateAppearanceTextStatus }] =
    useUpdateAppearanceTextStatusMutation();

  const { refetch: refetchGetWidgetAppearanceText } = useGetWidgetAppearanceTextQuery({
    shop: config.shop,
    offset: (widgetTextPaginationSetting.page - 1) * widgetTextPaginationSetting.perPage,
    limit: widgetTextPaginationSetting.perPage,
  });

  const { refetch: refecthGetFullWidgetAppearanceText } = useGetWidgetAppearanceTextQuery({
    shop: config.shop,
  });

  const resourceName = {
    singular: 'widget text',
    plural: 'widget text',
  };

  const headingsIndexTable = useMemo(
    () => [
      {
        title: t('manageLanguages.translationTable.languageColumn'),
        id: 'trans-language',
      },
      {
        title: t('manageLanguages.translationTable.codeColumn'),
        id: 'trans-code',
      },
      {
        title: t('manageLanguages.translationTable.statusColumn'),
        id: 'trans-status',
      },
      {
        title: t('manageLanguages.translationTable.actionColumn'),
        id: 'trans-action',
      },
    ],
    [t],
  );

  const widgetTextTableSettingsFilter = useMemo(
    () => widgetTextTableSettings?.filter((item) => !item.defaultLanguage) || [],
    [widgetTextTableSettings],
  );

  const { selectedResources, handleSelectionChange } = useIndexResourceState(widgetTextTableSettingsFilter, {
    resourceIDResolver: (resource) => resource.code,
  });

  const bulkActions = [
    {
      content: t('manageLanguages.translationTable.bulkAction.publish'),
      onAction: () => {
        updateAppearanceTextStatus({
          shop: config.shop,
          code: selectedResources,
          status: true,
        }).then(() => refetchGetWidgetAppearanceText());
      },
    },
    {
      content: t('manageLanguages.translationTable.bulkAction.unpublish'),
      onAction: () => {
        updateAppearanceTextStatus({
          shop: config.shop,
          code: selectedResources,
          status: false,
        }).then(() => refetchGetWidgetAppearanceText());
      },
    },
    {
      content: (
        <RegularText tone="critical" variant="bodySm" fontWeight="medium">
          {t('manageLanguages.translationTable.bulkAction.delete')}
        </RegularText>
      ) as unknown as string,
      onAction: () => {
        deleteAppearanceText({
          shop: config.shop,
          code: selectedResources,
        }).then(() => {
          refetchGetWidgetAppearanceText();
          refecthGetFullWidgetAppearanceText();
        });
      },
    },
  ];

  return (
    <Box paddingBlockStart="400">
      <Card padding="400">
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <InlineStack align="start" gap="200">
              <Box>
                <Icon source={LanguageTranslateIcon} />
              </Box>
              <BlockStack gap="100">
                <BoldText>{t('manageLanguages.title')}</BoldText>
                <RegularText>{t('manageLanguages.description')}</RegularText>
              </BlockStack>
            </InlineStack>

            <BlockStack gap="100" inlineAlign="end">
              {widgetTextDisable.isDisableManageLanguage && <PlanMessage plan={PricingPlan.Smart} />}
              <Button icon={PlusIcon} onClick={handleAddLanguage} disabled={widgetTextDisable.isDisableManageLanguage}>
                {t('manageLanguages.addLanguageButton')}
              </Button>
            </BlockStack>
          </InlineStack>
        </BlockStack>

        <Box paddingBlockStart={isOpenLanguageTableInstructionBanner ? '400' : '0'}>
          <InformationBanner
            isVisible={isOpenLanguageTableInstructionBanner}
            onDismiss={() => dispatch(handleVisibleBanner({ key: 'isOpenLanguageTableInstructionBanner', value: false }))}
          >
            Update the content for each language and publish it to show the store. If not published, the default language will
            appear.
          </InformationBanner>
        </Box>

        <Box paddingBlockStart="400">
          <Card>
            <BlockStack>
              <IndexTable
                key={'translation-table'}
                headings={headingsIndexTable as NonEmptyArray<IndexTableHeading>}
                itemCount={widgetTextTableSettingsFilter.length}
                resourceName={resourceName}
                emptyState={
                  <EmptyState
                    heading={t('manageLanguages.emptyState.title')}
                    action={{
                      content: t('manageLanguages.addLanguageButton'),
                      onAction: handleAddLanguage,
                    }}
                    image={THUMBNAIL.EMPTY_STATE_TRANSLATION_IMG}
                  >
                    <RegularText>{t('manageLanguages.emptyState.description')}</RegularText>
                  </EmptyState>
                }
                loading={isLoadingTranslationTable || isLoadingDeleteAppearanceText || isLoadingUpdateAppearanceTextStatus}
                selectedItemsCount={selectedResources.length}
                onSelectionChange={handleSelectionChange}
                selectable={!widgetTextDisable.isDisableManageLanguage}
                promotedBulkActions={bulkActions}
                bulkActions={useBreakpoints().mdDown ? bulkActions : []}
              >
                {widgetTextTableSettingsFilter.map((item, index) => (
                  <IndexTable.Row
                    id={item.code}
                    key={item.code}
                    position={index}
                    selected={selectedResources.includes(item.code)}
                  >
                    <RowData
                      dataSource={item}
                      index={index}
                      key={`${index}-${item.code}`}
                      disabled={widgetTextDisable.isDisableManageLanguage}
                    />
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </BlockStack>
            {widgetTextTableSettingsFilter.length !== 0 && (
              <InlineStack align="end">
                <Box padding={'300'}>
                  <Pagination
                    hasNext={widgetTextPaginationSetting.hasNext}
                    onNext={() => {
                      dispatch(handleSaveWidgetTextPaginationSettings({ page: widgetTextPaginationSetting.page + 1 }));
                    }}
                    hasPrevious={widgetTextPaginationSetting.hasPrevious}
                    onPrevious={() => {
                      dispatch(handleSaveWidgetTextPaginationSettings({ page: widgetTextPaginationSetting.page - 1 }));
                    }}
                  />
                </Box>
              </InlineStack>
            )}
          </Card>
        </Box>
      </Card>
      <AddLanguageModal
        fullWidgetAppearanceText={fullWidgetAppearanceText}
        open={isShowAddLanguageModal}
        onCancel={() => setIsShowAddLanguageModal(false)}
      />
    </Box>
  );
};

export default ManageLanguage;
